import getConfig from 'next/config'
import { isServer } from '@src/utils/isServer'

/*  We need to use these exact values to integrate with Optimizely's logging system
Comes from node_modules/@optimizely/optimizely-sdk/dist/modules/logging/models.d.ts
They do not export it.
*/
const LogLevel = {
  NOTSET: 0,
  DEBUG: 1,
  INFO: 2,
  WARNING: 3,
  ERROR: 4,
}

export const customOptlyLogger = (level, message): void => {
  const {
    publicRuntimeConfig: {
      optimizelyFeatureFlagId,
      optimizelyDisableLogging,
      optimizelyOnlyErrors,
    },
  } = getConfig()

  /*
    It can be handy to know if Optimizely isn't executing properly because it lacks and SDK key or datafile.
    However these logs can be extremely verbose and chirpy. Especially on the server which creates other issues.
    So if logging is enabled, but their is no SDK key, we want to bubble up on the client that you're missing the SDK key.
    Which is likely the root cause of your issues.

    Also only log errors at this point since other informational data won't be reliable since missing the SDK key means APIs aren't properly hit.
  */
  if (
    !optimizelyDisableLogging &&
    !optimizelyFeatureFlagId &&
    !isServer() &&
    level === LogLevel.ERROR
  ) {
    console.error(
      `${message} | Root cause is likely the lack of an Optimizely SDK key in the config.`
    )
  }

  // No optly logs if disabled or no SDK/Feature Ex key is set.
  // Disabling all logs takes precedent over only logging errors.
  if (optimizelyDisableLogging || !optimizelyFeatureFlagId) {
    return
  }

  // If we only want to capture errors, under otherwise normal runtime circumstances
  // Then we drop into this IF statement and capture from there if it is an error
  // Log the error if so and then return void to prevent further logging.
  if (optimizelyOnlyErrors) {
    if (level === LogLevel.ERROR) {
      console.error(`[ERRORS ONLY] ${message}`)
    }

    return
  }

  if (level === LogLevel.ERROR) {
    console.error(message)

    return
  }

  if (level === LogLevel.WARNING) {
    console.warn(message)

    return
  }

  // Optimizely spits out A LOT of decision making info when you let it.
  // This can be very helpful for debugging in the client
  // But absolutely floods the remote server logs.
  // So these should only bubble up on the client.
  // On the client they can also be silenced with the SDK browser extension.
  if (level === LogLevel.INFO || level === LogLevel.DEBUG) {
    if (isServer()) return

    console.info(message)

    return
  }

  console.info(`Unknown Optly Log Level: ${level} | ${message}`)
}

/* eslint-disable */
/**
 * Optimizely should never run for bots and won't be instantiated or send any events.
 * As such we expect it's logger to swallow all output and return nothing.
 * So we call this no-op function when a bot session is detected.
 *
 * @param level
 * @param message
 * @returns void
 */
export const noOpTrashLogs = (level, message) => {
  // No op function to swallow Optimizely logs for bots.
  // See best no op practices discussion here: https://stackoverflow.com/questions/21634886/what-is-the-javascript-convention-for-no-operation
}

/* eslint-enable */
